<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { DirhamIcon } from '@uiKit/icons'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import PropertyDetailSection from './PropertyDetailSection.vue'
import type { BreakdownGroup } from '../utils/propertyFinancialUtils'

// INVESTMENT BREAKDOWN (Figma 32590:61607): a collapsible "Income Breakdown"
// card holding grey groups — rent, yields, projected returns. Every row has
// an ⓘ explainer; amounts carry the dirham glyph.

interface Props {
  groups: BreakdownGroup[]
}

defineProps<Props>()

const isOpen = ref(true)
</script>

<template>
  <PropertyDetailSection v-if="groups.length" title="Investment Breakdown">
    <Box direction="col" gap="3" class="pd-breakdown">
      <Box
        is="button"
        type="button"
        align="center"
        justify="between"
        class="pd-breakdown__toggle"
        :aria-expanded="isOpen"
        @click="isOpen = !isOpen"
      >
        <Text type="heading" weight="medium" is="span" style="line-height: 24px"
          >Income Breakdown</Text
        >
        <Box align="center" justify="center" class="pd-breakdown__chevron-box">
          <PiIcon :name="isOpen ? 'chevron-up' : 'chevron-down'" class="pd-breakdown__chevron" />
        </Box>
      </Box>

      <template v-if="isOpen">
        <Box
          v-for="group in groups"
          :key="group.key"
          direction="col"
          gap="2"
          class="pd-breakdown__group"
        >
          <Box
            v-for="(row, index) in group.rows"
            :key="row.key"
            align="center"
            justify="between"
            gap="3"
            class="pd-breakdown__row"
            :class="{ 'pd-breakdown__row--last': index === group.rows.length - 1 }"
          >
            <Box align="center" gap="6px" class="pd-breakdown__label">
              <Text type="body" color="var(--p-surface-700)" is="span">{{ row.label }}</Text>
              <InfoSheetButton
                v-if="row.sheet"
                :content="row.sheet"
                :label="`About ${row.label}`"
              />
            </Box>
            <Box direction="col" align="end" gap="2">
              <Box align="center" gap="5px">
                <Text v-if="row.isDeduction" type="body" weight="semibold" is="span">-</Text>
                <Box align="center" gap="6px">
                  <DirhamIcon v-if="row.isAmount" class="pd-breakdown__glyph" />
                  <Text
                    type="heading"
                    weight="semibold"
                    is="span"
                    style="font-variant-numeric: tabular-nums"
                  >
                    {{ row.value }}
                  </Text>
                </Box>
              </Box>
              <Text v-if="row.subValue" type="body" color="var(--p-surface-600)" is="span">
                {{ row.subValue }}
              </Text>
            </Box>
          </Box>
        </Box>
      </template>
    </Box>
  </PropertyDetailSection>
</template>

<style scoped>
.pd-breakdown {
  padding: 16px;
  border: 1px solid var(--p-primary-100);
  border-radius: 14px;
  background: var(--p-surface-0);
}

.pd-breakdown__toggle {
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
}

.pd-breakdown__toggle:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 4px;
  border-radius: 4px;
}

.pd-breakdown__chevron-box {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.pd-breakdown__chevron {
  font-size: 14px;
  color: var(--p-primary-600);
}

.pd-breakdown__group {
  padding: 12px;
  border-radius: 8px;
  background: var(--p-surface-50);
}

.pd-breakdown__row {
  padding-bottom: 10px;
  border-bottom: 1px solid var(--p-surface-200);
}

/* The last row drops its divider but keeps the 10px bottom pad (Figma). */
.pd-breakdown__row--last {
  border-bottom: none;
}

.pd-breakdown__label {
  min-width: 0;
}

.pd-breakdown__glyph {
  width: 14px;
  height: 13px;
  color: var(--p-text-color);
}
</style>
